/**
 * Add / edit an event, with the live expected-waste readout and the conflict decision step
 * (POS2-1259 CP4).
 *
 * The drawer has two states: the form, and — after the API answers 409 — the decision step.
 * They are one component because the decision is about the form's own values: choosing "Change
 * the details" has to come back to exactly what was typed, which a separate dialog would lose.
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  EVENT_TYPES,
  type EventType,
  type EventWrite,
  type OccupancyEvent,
  type ServiceStyle,
  type WasteBasis,
} from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { SERVICE_STYLES, TYPE_LABEL_KEYS, SERVICE_STYLE_LABEL_KEYS } from "@/lib/occupancy-labels";
import {
  defaultEndTime,
  formatExpectedWaste,
  maskTimeOfDay,
  parseTimeOfDay,
} from "@/lib/occupancy";

export type DrawerState =
  | { mode: "closed" }
  | { mode: "add"; date: string }
  | { mode: "edit"; event: OccupancyEvent };

type Form = {
  name: string;
  event_type: EventType;
  date: string;
  covers: string;
  start_time: string;
  end_time: string;
  room: string;
  service_style: ServiceStyle | "";
  notes: string;
};

/**
 * A new event opens on the working day (Artem, POS2-1259). Most bookings are daytime functions,
 * and a prefilled pair is quicker to adjust than an empty one is to fill.
 *
 * Only ever on a *blank* form: `formFrom` keeps an existing event's own times, so editing a
 * booking that was saved without times does not invent a nine-to-five it never had.
 */
const DEFAULT_START = "09:00";
const DEFAULT_END = "17:00";

function blankForm(date: string): Form {
  return {
    name: "",
    event_type: "function",
    date,
    covers: "",
    start_time: DEFAULT_START,
    end_time: DEFAULT_END,
    room: "",
    service_style: "",
    notes: "",
  };
}

function formFrom(event: OccupancyEvent): Form {
  return {
    name: event.name,
    event_type: event.event_type,
    date: event.date,
    covers: String(event.covers),
    start_time: event.start_time?.slice(0, 5) ?? "",
    end_time: event.end_time?.slice(0, 5) ?? "",
    room: event.room ?? "",
    service_style: event.service_style ?? "",
    notes: event.notes ?? "",
  };
}

function toWrite(form: Form, siteId: string): EventWrite {
  // Parsed here as well as on blur. Clicking Save blurs the field first, but the click handler
  // closes over the previous render's form, so the raw text is what would otherwise reach the
  // API — "1830:00" rather than "18:30:00".
  const start = parseTimeOfDay(form.start_time);
  const end = parseTimeOfDay(form.end_time);
  return {
    site_id: siteId,
    date: form.date,
    name: form.name.trim(),
    event_type: form.event_type,
    covers: Number(form.covers || 0),
    // Empty means "not stated", which the schema takes as null — an empty string would fail
    // the API's time parsing rather than reading as absent.
    start_time: start && `${start}:00`,
    end_time: end && `${end}:00`,
    room: form.room.trim() || null,
    service_style: form.service_style || null,
    notes: form.notes.trim() || null,
  };
}

export function EventDrawer({
  state,
  siteId,
  basis,
  dayTotal,
  clashing,
  isSaving,
  onSave,
  onReplace,
  onBookBoth,
  onDismissConflict,
  onDelete,
  onClose,
}: {
  state: DrawerState;
  siteId: string;
  basis: WasteBasis | undefined;
  /** The selected day's current total, so "Book both" can show the resulting figure first. */
  dayTotal: number;
  clashing: OccupancyEvent[] | null;
  isSaving: boolean;
  onSave: (body: EventWrite) => void;
  onReplace: (body: EventWrite, clash: OccupancyEvent) => void;
  onBookBoth: (body: EventWrite) => void;
  onDismissConflict: () => void;
  onDelete: (event: OccupancyEvent) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>(() => blankForm(new Date().toISOString().slice(0, 10)));

  useEffect(() => {
    if (state.mode === "add") setForm(blankForm(state.date));
    if (state.mode === "edit") setForm(formFrom(state.event));
  }, [state]);

  if (state.mode === "closed") return null;

  const covers = Number(form.covers || 0);
  const expected = basis ? formatExpectedWaste(covers, basis) : null;
  const set = (patch: Partial<Form>) => setForm((current) => ({ ...current, ...patch }));
  /** Normalise to 24-hour on the way out of the field, and move the end to two hours on when it
   *  is missing or would sit before the start — the prefilled 17:00 against an 18:30 start, say,
   *  which would otherwise save an event that ends before it begins. An end that already makes
   *  sense against the start is left alone, however it got there. */
  const commitStart = (raw: string) => {
    const start = parseTimeOfDay(raw);
    if (start === null) return set({ start_time: "" });
    const keepsEnd = form.end_time !== "" && form.end_time > start;
    set({ start_time: start, end_time: keepsEnd ? form.end_time : defaultEndTime(start) });
  };
  const canSave = form.name.trim().length > 0 && form.covers !== "" && covers >= 0;

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>
            {state.mode === "edit"
              ? t("occupancy.drawer.editTitle")
              : t("occupancy.drawer.addTitle")}
          </SheetTitle>
        </SheetHeader>

        {clashing && clashing.length > 0 ? (
          <div className="flex flex-col gap-4 p-4">
            <div>
              <h3 className="text-sm font-semibold">{t("occupancy.conflict.title")}</h3>
              {clashing.map((clash) => (
                <p key={clash.id} className="mt-1 text-[13px] text-muted-foreground">
                  {t("occupancy.conflict.description", {
                    name: clash.name,
                    room: clash.room ?? "",
                    start: clash.start_time?.slice(0, 5) ?? "",
                    end: clash.end_time?.slice(0, 5) ?? "",
                    covers: clash.covers,
                  })}
                </p>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                className="h-auto flex-col items-start gap-0.5 py-2.5 text-left"
                disabled={isSaving}
                onClick={() => onReplace(toWrite(form, siteId), clashing[0])}
              >
                <span className="font-semibold">{t("occupancy.conflict.replace")}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {t("occupancy.conflict.replaceHint")}
                </span>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col items-start gap-0.5 py-2.5 text-left"
                disabled={isSaving}
                onClick={() => onBookBoth(toWrite(form, siteId))}
              >
                <span className="font-semibold">{t("occupancy.conflict.bookBoth")}</span>
                {/* The resulting total, before choosing — the spec asks for the figure up front. */}
                <span className="text-xs font-normal text-muted-foreground">
                  {t("occupancy.conflict.bookBothHint", { total: dayTotal + covers })}
                </span>
              </Button>
              <Button variant="ghost" onClick={onDismissConflict}>
                {t("occupancy.conflict.changeDetails")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 p-4">
            <label className="flex flex-col gap-1 text-[13px] font-medium">
              {t("occupancy.drawer.name")}
              <Input
                value={form.name}
                placeholder={t("occupancy.drawer.namePlaceholder")}
                onChange={(e) => set({ name: e.target.value })}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-[13px] font-medium">
                {t("occupancy.drawer.type")}
                <Select
                  value={form.event_type}
                  onValueChange={(value) => set({ event_type: value as EventType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(TYPE_LABEL_KEYS[type])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="flex flex-col gap-1 text-[13px] font-medium">
                {t("occupancy.drawer.covers")}
                <Input
                  type="number"
                  min={0}
                  value={form.covers}
                  onChange={(e) => set({ covers: e.target.value })}
                />
              </label>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="flex flex-col gap-1 text-[13px] font-medium">
                {t("occupancy.drawer.date")}
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => set({ date: e.target.value })}
                />
              </label>
              <label className="flex flex-col gap-1 text-[13px] font-medium">
                {t("occupancy.drawer.start")}
                <Input
                  inputMode="numeric"
                  maxLength={5}
                  placeholder={t("occupancy.drawer.timePlaceholder")}
                  value={form.start_time}
                  // Selected on focus because the field arrives prefilled: typing into "09:00"
                  // otherwise appends to it, which is how it came to read "00009323". The
                  // mouseup is swallowed because it would otherwise collapse that selection to a
                  // caret before the first keystroke lands.
                  onFocus={(e) => e.target.select()}
                  onMouseUp={(e) => e.preventDefault()}
                  onChange={(e) => set({ start_time: maskTimeOfDay(e.target.value) })}
                  onBlur={(e) => commitStart(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-[13px] font-medium">
                {t("occupancy.drawer.end")}
                <Input
                  inputMode="numeric"
                  maxLength={5}
                  placeholder={t("occupancy.drawer.timePlaceholder")}
                  value={form.end_time}
                  onFocus={(e) => e.target.select()}
                  onMouseUp={(e) => e.preventDefault()}
                  onChange={(e) => set({ end_time: maskTimeOfDay(e.target.value) })}
                  onBlur={(e) => set({ end_time: parseTimeOfDay(e.target.value) ?? "" })}
                />
              </label>
            </div>

            <label className="flex flex-col gap-1 text-[13px] font-medium">
              {t("occupancy.drawer.room")}
              {/* Free text, deliberately: the spec overrides the mock's dropdown, because a
                  managed room list is out of scope. */}
              <Input
                value={form.room}
                placeholder={t("occupancy.drawer.roomPlaceholder")}
                onChange={(e) => set({ room: e.target.value })}
              />
              <span className="text-xs font-normal text-muted-foreground">
                {t("occupancy.drawer.roomHint")}
              </span>
            </label>

            <label className="flex flex-col gap-1 text-[13px] font-medium">
              {t("occupancy.drawer.serviceStyle")}
              <Select
                value={form.service_style || undefined}
                onValueChange={(value) => set({ service_style: value as ServiceStyle })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_STYLES.map((style) => (
                    <SelectItem key={style} value={style}>
                      {t(SERVICE_STYLE_LABEL_KEYS[style])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-[13px] font-medium">
              {t("occupancy.drawer.notes")}
              <textarea
                className="min-h-16 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </label>

            {expected && (
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-[13px] font-medium">
                    {t("occupancy.drawer.expectedWaste")}
                  </span>
                  <span className="text-sm font-bold tabular-nums">{expected.headline}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {basis?.source === "site_average"
                    ? t("occupancy.drawer.basisSiteAverage", { grams: expected.gramsPerCover })
                    : t("occupancy.drawer.basisTarget", { grams: expected.gramsPerCover })}
                </p>
              </div>
            )}

            <div className="mt-1 flex items-center gap-2">
              <Button disabled={!canSave || isSaving} onClick={() => onSave(toWrite(form, siteId))}>
                {t("occupancy.action.save")}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                {t("occupancy.action.cancel")}
              </Button>
              {state.mode === "edit" && (
                <Button
                  variant="ghost"
                  className="ml-auto text-destructive hover:text-destructive"
                  onClick={() => onDelete(state.event)}
                >
                  {t("occupancy.action.delete")}
                </Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
